import { useState } from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Plus, Trash2, ChevronUp, ChevronDown } from 'lucide-react';
import type { IFormSection, IFormItem } from '@/@types/claims';

// ─── ItemRow ─────────────────────────────────────────────────────────────────

interface IItemRowProps {
  item: IFormItem;
  isFirst: boolean;
  isLast: boolean;
  onUpdate: (patch: Partial<IFormItem>) => void;
  onRemove: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
}

function ItemRow({ item, isFirst, isLast, onUpdate, onRemove, onMoveUp, onMoveDown }: IItemRowProps) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  return (
    <>
      <div className="grid grid-cols-12 gap-2 items-start py-2 border-b last:border-0">
        <div className="col-span-3">
          <Input placeholder="Item name" value={item.name} onChange={e => onUpdate({ name: e.target.value })} className="h-8 text-sm" />
        </div>
        <div className="col-span-2">
          <Input placeholder="Plan pays %" type="number" value={item.planPaysPercent}
            onChange={e => onUpdate({ planPaysPercent: e.target.value })} className="h-8 text-sm" />
        </div>
        <div className="col-span-2">
          <Input placeholder="You pay %" type="number" value={item.youPayPercent}
            onChange={e => onUpdate({ youPayPercent: e.target.value })} className="h-8 text-sm" />
        </div>
        <div className="col-span-2">
          <Input placeholder="Annual limit" type="number" value={item.annualLimit}
            onChange={e => onUpdate({ annualLimit: e.target.value })} className="h-8 text-sm" />
        </div>
        <div className="col-span-2">
          <Input placeholder="Visit limit" type="number" value={item.visitLimit}
            onChange={e => onUpdate({ visitLimit: e.target.value })} className="h-8 text-sm" />
        </div>
        <div className="col-span-1 flex gap-1 justify-end">
          <Button size="icon" variant="ghost" className="h-8 w-8" disabled={isFirst} onClick={onMoveUp}><ChevronUp className="h-3 w-3" /></Button>
          <Button size="icon" variant="ghost" className="h-8 w-8" disabled={isLast} onClick={onMoveDown}><ChevronDown className="h-3 w-3" /></Button>
          <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => setConfirmOpen(true)}><Trash2 className="h-3 w-3" /></Button>
        </div>
      </div>
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove item?</AlertDialogTitle>
            <AlertDialogDescription>&ldquo;{item.name || 'Untitled item'}&rdquo; will be removed.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={onRemove} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// ─── SectionEditor ───────────────────────────────────────────────────────────

interface ISectionEditorProps {
  section: IFormSection;
  isFirst: boolean;
  isLast: boolean;
  onUpdateName: (name: string) => void;
  onRemove: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onAddItem: () => void;
  onUpdateItem: (itemTempId: string, patch: Partial<IFormItem>) => void;
  onRemoveItem: (itemTempId: string) => void;
  onMoveItemUp: (itemTempId: string) => void;
  onMoveItemDown: (itemTempId: string) => void;
}

export function SectionEditor({
  section, isFirst, isLast,
  onUpdateName, onRemove, onMoveUp, onMoveDown,
  onAddItem, onUpdateItem, onRemoveItem, onMoveItemUp, onMoveItemDown,
}: ISectionEditorProps) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  return (
    <>
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Input placeholder="Section name" value={section.name} onChange={e => onUpdateName(e.target.value)} className="flex-1 font-medium" />
            <Button size="icon" variant="ghost" disabled={isFirst} onClick={onMoveUp}><ChevronUp className="h-4 w-4" /></Button>
            <Button size="icon" variant="ghost" disabled={isLast} onClick={onMoveDown}><ChevronDown className="h-4 w-4" /></Button>
            <Button size="icon" variant="ghost" className="text-destructive" onClick={() => setConfirmOpen(true)}><Trash2 className="h-4 w-4" /></Button>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          {section.items.length > 0 && (
            <div className="mb-3">
              <div className="grid grid-cols-12 gap-2 text-xs font-medium text-muted-foreground pb-1 border-b">
                <span className="col-span-3">Name</span>
                <span className="col-span-2">Plan pays %</span>
                <span className="col-span-2">You pay %</span>
                <span className="col-span-2">Annual limit</span>
                <span className="col-span-2">Visits</span>
                <span className="col-span-1" />
              </div>
              {section.items.map((item, idx) => (
                <ItemRow key={item.tempId} item={item}
                  isFirst={idx === 0} isLast={idx === section.items.length - 1}
                  onUpdate={patch => onUpdateItem(item.tempId, patch)}
                  onRemove={() => onRemoveItem(item.tempId)}
                  onMoveUp={() => onMoveItemUp(item.tempId)}
                  onMoveDown={() => onMoveItemDown(item.tempId)}
                />
              ))}
            </div>
          )}
          <Button size="sm" variant="outline" onClick={onAddItem}><Plus className="mr-1 h-3 w-3" /> Add Item</Button>
        </CardContent>
      </Card>
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove section?</AlertDialogTitle>
            <AlertDialogDescription>&ldquo;{section.name || 'Untitled section'}&rdquo; and all its items will be removed.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={onRemove} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
